import { Inject, Injectable, InjectionToken } from '@angular/core';
import { captureMessage, Scope } from '@sentry/angular';
import { IValidation } from 'src/app/models';
import { environment } from 'src/environments/environment';

export type CaptureMessage = (message: string, configureScope: (scope: Scope) => Scope) => string;

/**
 * Sentry's captureMessage, behind a token. Injected rather than called directly because
 * jest.spyOn cannot redefine a property on the ESM namespace object — it throws
 * "Cannot redefine property: captureMessage" on a fresh install.
 */
export const CAPTURE_MESSAGE = new InjectionToken<CaptureMessage>('CAPTURE_MESSAGE', {
  factory: () => captureMessage as CaptureMessage,
});

/**
 * Sends a validation rule's FAILED diagnostics to Sentry when the user asks for it.
 *
 * User-triggered rather than automatic: a rule that cannot run is routine for an in-progress
 * element, so capturing every occurrence would bury real bugs — which is what
 * `sentry-event-filter.ts` exists to prevent.
 *
 * This exists because the elements that fail are typically unreleased, and the developers who
 * would debug them are not permitted to see their data. The report therefore carries only the
 * environment, the rule, and the counts the rule already put in `diagnostics` — never M-code
 * values, characteristic text, or anything else from the design.
 */
@Injectable({ providedIn: 'root' })
export class ValidationDiagnosticsReportService {
  public constructor(@Inject(CAPTURE_MESSAGE) private readonly capture: CaptureMessage) {}

  public report(validation: IValidation): string {
    return this.capture(`Validation could not run: ${validation.title}`, (scope) => {
      scope.setLevel('warning');

      // Group every report for one rule into a single Sentry issue, rather than one per
      // distinct error message.
      scope.setFingerprint(['validation-not-possible', validation.id]);

      scope.setTags({
        'emmet.environment': environment.environmentName,
        'emmet.validation': validation.id,
      });

      // Deliberately not validation.error: it is free-form prose that interpolates whatever a
      // rule chooses, so including it would make the no-business-data guarantee depend on
      // every future message being careful. `diagnostics.reason` carries the same "which
      // branch fired" information as a fixed value.
      scope.setContext('validation', {
        rule: validation.id,
        title: validation.title,
      });

      if (validation.diagnostics) {
        scope.setContext('diagnostics', { ...validation.diagnostics });
      }

      return scope;
    });
  }
}
